// file: client/src/App.tsx
/**
 * PHARMAKON — Root Application Router v4
 *
 * Route additions in v4:
 *  /avoirs           → JournalAvoirsPage (Journal des avoirs fournisseur, refonte WP-30)
 *  /avoirs/nouveau   → NouvelAvoirPage (avoir editor, refonte WP-29)
 *  /avoirs/:id       → JournalAvoirsPage (journal with the detail drawer open)
 *  /achats/bons-retour          → JournalBonsRetourPage (Journal des bons de retour, refonte WP-31)
 *  /achats/bons-retour/nouveau  → BonRetourEditeurPage (bon de retour editor)
 *  /achats/bons-retour/:id      → BonRetourPage (brouillon → editor, else the document)
 *  /achats/reclamations          → JournalReclamationsPage (Journal des réclamations, refonte WP-35)
 *  /achats/reclamations/nouvelle → ReclamationEditeurPage (full-page composer, URL pre-fill)
 *  /achats/reclamations/:id      → ReclamationPage (brouillon → composer, else the document)
 */
import { useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastHost } from "./components/ui/primitives";
import { useAuthStore } from "./stores/authStore.js";
import { useThemeStore } from "./stores/themeStore.js";
// Module 09 — offline mode (engine boots once; the gate is inert when the
// deployment flag is off or the terminal isn't enrolled).
import { startSyncEngine } from "./offline/syncEngine.js";
import SyncConflictsPage from "./offline/SyncConflictsPage.jsx";
import { ToastProvider } from "./context/ToastContext.jsx";
import AppShell from "./components/layout/AppShell.jsx";
import { NotFoundPage } from "./components/layout/NotFoundPage";
// WP-18 — one route-access table for the guards and the menu (ux/01 §6.6).
import { ROUTE_REDIRECTS, routeRoles } from "./config/routeAccess.config.js";
import { redirectionEmplacements } from "./lib/magasins.js";
import { REDIRECTIONS_REGLEMENTS } from "./lib/tresorerie/reglements.js";

// Pages — grouped by domain
import LoginPage from "./pages/auth/LoginPage.jsx";
import RegisterPage from "./pages/auth/RegisterPage.jsx";
import RegisterFournisseur from "./pages/auth/RegisterFournisseur.jsx";
import TermsPage from "./pages/auth/TermsPage.jsx";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage.jsx";
// X XWP-72 — the shared team login (CP §15.3): « Connexion équipe » and the user picker, public like
// /login (the workstation's team token is not a user session); a browser holding an open workstation
// and no picked user goes to the picker, never to /login.
import { ROUTES_SESSION_EQUIPE } from "./config/session-equipe.config.js";
import { posteOuvert } from "./lib/session-equipe.js";
const ConnexionEquipe = lazy(() => import("./pages/equipe-session/ConnexionEquipe.jsx"));
const ChoixUtilisateur = lazy(() => import("./pages/equipe-session/ChoixUtilisateur.jsx"));
// Design-system port (2026-05) — DashboardRouter replaces the per-role
// legacy dashboards. Routes use the new dashboard regardless of role.
// (Campaign 3: the orphaned legacy ProprietaireDashboard/CaissierDashboard
// imports were dropped — no route referenced them and they bloated the
// bundle. The files remain on disk, token-migrated, if ever re-routed.)
const DashboardRouter = lazy(() => import("./pages/dashboards/Dashboards.jsx"));
// New design pages added in this batch
const EquipePage = lazy(() => import("./pages/equipe/EquipePage.jsx"));
const SupportPage = lazy(() => import("./pages/support/SupportPage.jsx"));
// Journal d'audit + Abonnement — design-system port of legacy admin pages
// surfaced for PROPRIETAIRE / MANAGER under their own top-level routes.
const AuditPage = lazy(() => import("./pages/audit/AuditPage.jsx"));
const AbonnementPage = lazy(() => import("./pages/abonnement/AbonnementPage.jsx"));
const CaisseLibre = lazy(() => import("./pages/vente/CaisseLibre.jsx"));
const VenteChifa = lazy(() => import("./pages/vente/VenteChifa.jsx"));
const VenteList = lazy(() => import("./pages/vente/VenteList.jsx"));
const JournalAchats = lazy(() => import("./pages/achat/JournalAchats.jsx"));
const JournalDetailleAchats = lazy(() => import("./pages/achat/JournalDetailleAchats.jsx"));
// Refonte WP-22 — S1 « Nouvel achat » (design/ux/02_achats.md §4), one page for both routes.
const NouvelAchatPage = lazy(() => import("./pages/achat/nouveau/NouvelAchatPage.jsx"));
// Refonte WP-25 — S3 « Facture d'achat » (ux/02 §6) replaces the legacy AchatDetail page.
const FactureAchatPage = lazy(() => import("./pages/achat/fiche/FactureAchatPage.jsx"));
const EFacturesReceived = lazy(() => import("./pages/achat/EFacturesReceived.jsx"));
const EFactureRecuDetail = lazy(() => import("./pages/achat/EFactureRecuDetail.jsx"));
const JournalAvoirsPage = lazy(() => import("./pages/avoir/journal/JournalAvoirsPage.jsx"));
const NouvelAvoirPage = lazy(() => import("./pages/avoir/nouveau/NouvelAvoirPage.jsx"));
// Refonte WP-31 (D-22, ux/03 §4) — bons de retour fournisseur
const JournalBonsRetourPage = lazy(() => import("./pages/bon-retour/JournalBonsRetourPage.jsx"));
const BonRetourEditeurPage = lazy(() => import("./pages/bon-retour/BonRetourEditeurPage.jsx"));
const BonRetourPage = lazy(() => import("./pages/bon-retour/BonRetourPage.jsx"));
const JournalReclamationsPage = lazy(() => import("./pages/reclamation/JournalReclamationsPage.jsx"));
const ReclamationEditeurPage = lazy(() => import("./pages/reclamation/ReclamationEditeurPage.jsx"));
const ReclamationPage = lazy(() => import("./pages/reclamation/ReclamationPage.jsx"));
const StockDashboard = lazy(() => import("./pages/stock/StockDashboard.jsx"));
const CorrectionsJournal = lazy(() => import("./pages/stock/CorrectionsJournal.jsx"));
// Batch 4 (Cluster D) — professional product search (all three pharmacy roles)
const RechercheProduitPage = lazy(() => import("./pages/recherche/RechercheProduitPage.jsx"));
const ProduitList = lazy(() => import("./pages/produit/ProduitList.jsx"));
const ProduitForm = lazy(() => import("./pages/produit/ProduitForm.jsx"));
const FournisseurList = lazy(() => import("./pages/fournisseur/FournisseurList.jsx"));
const FournisseurForm = lazy(() => import("./pages/fournisseur/FournisseurForm.jsx"));
const PharmacieSettings = lazy(() => import("./pages/parametres/PharmacieSettings.jsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.jsx"));
const AccountsQueue = lazy(() => import("./pages/admin/AccountsQueue.jsx"));
const AdminFournisseurs = lazy(() => import("./pages/admin/AdminFournisseurs.jsx"));
const AdminLicences = lazy(() => import("./pages/admin/AdminLicences.jsx"));
// Fournisseur ERP Module 08 — the supplier-subscription platform dashboard.
const AbonnementsFournisseurs = lazy(() => import("./pages/admin/AbonnementsFournisseurs.jsx"));
const LicenceManager = lazy(() => import("./pages/admin/LicenceManager.jsx"));
const BillingDashboard = lazy(() => import("./pages/admin/BillingDashboard.jsx"));
const PricingConfig = lazy(() => import("./pages/admin/PricingConfig.jsx"));
const ModulesManager = lazy(() => import("./pages/admin/ModulesManager.jsx"));
const CNASDbManager = lazy(() => import("./pages/admin/CNASDbManager.jsx"));
const CNASImport = lazy(() => import("./pages/admin/CNASImport.jsx"));
// Module 4 — Monitoring
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth.jsx"));
const AuditExplorer = lazy(() => import("./pages/admin/AuditExplorer.jsx"));
const ErrorTracking = lazy(() => import("./pages/admin/ErrorTracking.jsx"));
// Module 5 — Users & Security
const UsersList = lazy(() => import("./pages/admin/UsersList.jsx"));
const SecurityLog = lazy(() => import("./pages/admin/SecurityLog.jsx"));
// Module 6 — Communications
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications.jsx"));
const NotifBroadcast = lazy(() => import("./pages/admin/NotifBroadcast.jsx"));
const TicketSupport = lazy(() => import("./pages/admin/TicketSupport.jsx"));

const MonCompte = lazy(() => import("./pages/parametres/MonCompte.jsx"));
const AideDecision = lazy(() => import("./pages/decision/AideDecision.jsx"));
const BesoinPage = lazy(() => import("./pages/decision/BesoinPage.jsx"));
const OffresPage = lazy(() => import("./pages/decision/OffresPage.jsx"));
const DecisionConfigPage = lazy(() => import("./pages/decision/DecisionConfigPage.jsx"));
import ModuleGate from "./components/billing/ModuleGate.jsx";
const MyTickets = lazy(() => import("./pages/support/MyTickets.jsx"));
const BaseProduits = lazy(() => import("./pages/admin/BaseProduits.jsx"));
const BaseProduitImport = lazy(() => import("./pages/admin/BaseProduitImport.jsx"));
const ReferentielsAalaji = lazy(() => import("./pages/admin/aalaji/ReferentielsAalaji.jsx"));
// F-2 (B2G) — the NOVERA macro-admin vertical consoles (Novanalyse / Cabinets / Ailaji).
const NovanalyseConsole = lazy(() => import("./pages/admin/verticals/NovanalyseConsole.jsx"));
const CabinetsConsole = lazy(() => import("./pages/admin/verticals/CabinetsConsole.jsx"));
const AilajiConsole = lazy(() => import("./pages/admin/verticals/AilajiConsole.jsx"));
const AalajiHub = lazy(() => import("./pages/aalaji/AalajiHub.jsx"));
const PreparationsConsole = lazy(() => import("./pages/aalaji/PreparationsConsole.jsx"));
const ParametresAalaji = lazy(() => import("./pages/aalaji/ParametresAalaji.jsx"));
// Fournisseur ERP portal (Module 00) — modular nested route tree replacing the
// monolithic FournisseurPortal shell. The old monolith is kept on disk until
// parity is verified (see routes/fournisseurRoutes.jsx).
import FournisseurRoutes from "./routes/fournisseurRoutes.jsx";
import { SUPPLIER_ROLES } from "./config/fournisseur.constants.js";

// Feature batch 2026-05 — Client mgmt, Crédit, État rotation
const ClientList = lazy(() => import("./pages/client/ClientList.jsx"));
// Phase 1 (XpertPharm parity) — psychotrope module
const MedecinList = lazy(() => import("./pages/medecin/MedecinList.jsx"));
const OrdonnancierJournal = lazy(() => import("./pages/ordonnancier/OrdonnancierJournal.jsx"));
const OrdonnancierAnnexe = lazy(() => import("./pages/ordonnancier/OrdonnancierAnnexe.jsx"));
// Phase 2 (XpertPharm parity) — shortage & reorder
const ManquantList = lazy(() => import("./pages/manquant/ManquantList.jsx"));
// Batch-1 vague 2 (Q2) — la surface de vieillissement des ventes de secours.
const SecoursPage = lazy(() => import("./pages/secours/SecoursPage.jsx"));
const OrderAssistant = lazy(() => import("./pages/manquant/OrderAssistant.jsx"));
// Phase 4 (XpertPharm parity) — expiry risk
const PeremptionRisk = lazy(() => import("./pages/stock/PeremptionRisk.jsx"));
// Phase 5 (XpertPharm parity) — loyalty programme
const FideliteHub = lazy(() => import("./pages/fidelite/FideliteHub.jsx"));
// Phase 7 (XpertPharm parity) — sales-document lifecycle
const FacturationHub = lazy(() => import("./pages/facturation/FacturationHub.jsx"));
// Phase 8 (XpertPharm parity) — treasury & cash management
const TresorerieHub = lazy(() => import("./pages/tresorerie/TresorerieHub.jsx"));
// Trésorerie TWP-22 — « Mouvements de ma caisse » (the caissier's own journal; P / M → /tresorerie/mouvements)
const MaCaissePage = lazy(() => import("./pages/tresorerie/MaCaissePage.jsx"));
// Batch-1 vague 3 (2026-08) — la CAISSE CENTRALE d'officine : le compte
// permanent, ses emplacements de fonds, son journal, ses charges.
const CaisseCentrale = lazy(() => import("./pages/tresorerie/CaisseCentrale.jsx"));
const ComptesCaissesPage = lazy(() => import("./pages/tresorerie/ComptesCaissesPage.jsx"));
const MouvementsPage = lazy(() => import("./pages/tresorerie/MouvementsPage.jsx"));
// Trésorerie TWP-26 — « Règlements » (G1 soldes fournisseurs, G3 journal des règlements, the G2 form, T2 / G6 drawers)
const ReglementsPage = lazy(() => import("./pages/tresorerie/ReglementsPage.jsx"));
// Phase 10 (XpertPharm parity) — financial reports
const RapportsFinance = lazy(() => import("./pages/rapports/RapportsFinance.jsx"));
// Phase 11 (XpertPharm parity) — multi-warehouse
// X XWP-16 — the one Magasins workspace (tree, emplacements, transferts); /emplacements lands on its view.
const MagasinsWorkspace = lazy(() => import("./pages/magasins/MagasinsWorkspace.jsx"));
// TIP (2026-08) — réseau de confrères / transferts inter-pharmacies.
const ConfreresHub = lazy(() => import("./pages/confreres/ConfreresHub.jsx"));
const ClientForm = lazy(() => import("./pages/client/ClientForm.jsx"));
const ClientDetail = lazy(() => import("./pages/client/ClientDetail.jsx"));
// X XWP-20 — « Crédits clients » journal + drawer /credits/:id (replaces CreditList.jsx).
const JournalCreditsPage = lazy(() => import("./pages/credit/JournalCreditsPage.jsx"));
const EtatRotationPage = lazy(() => import("./pages/decision/EtatRotationPage.jsx"));
// Phase 3 — Enterprise Analytics / BI platform
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage.jsx"));
// Feature batch 4 — médecin analytics drill-down
const MedecinDetail = lazy(() => import("./pages/analytics/MedecinDetail.jsx"));
// Commit-2 (2026-06) — Crédit rename + Instance à terme CHIFA + Retours
const JournalInstancesPage = lazy(() => import("./pages/instance-chifa/JournalInstancesPage.jsx"));
const InstanceChifaCreate = lazy(() => import("./pages/instance-chifa/InstanceChifaCreate.jsx"));
const RetourList = lazy(() => import("./pages/retour/RetourList.jsx"));
const NouveauRetour = lazy(() => import("./pages/retour/NouveauRetour.jsx"));
const RetourDetail = lazy(() => import("./pages/retour/RetourDetail.jsx"));
// ── E-Facture ERP module (2026-06) — lazy-loaded ──────────────────────────────
// Offer comparison + counter-offer (Aide à la Décision) and the e-facture
// workspace + emergency-order flow (Achats). Code-split behind the existing
// <Suspense fallback={<PageLoader />}> boundary.
const OfferComparisonPage = lazy(
  () => import("./pages/decision/OfferComparisonPage.jsx"),
);
const CounterOfferPage = lazy(
  () => import("./pages/decision/CounterOfferPage.jsx"),
);
const EmergencyOrderPage = lazy(
  () => import("./pages/decision/EmergencyOrderPage.jsx"),
);
const EFactureWorkspace = lazy(
  () => import("./pages/achat/EFactureWorkspace.jsx"),
);
// ── Module 03 (Commercial) — pharmacy-facing surfaces (2026-06) ───────────────
// Supplier-catalogue browse + order builder, RFQ broadcast/compare, bilateral
// BC confirm, délégué Gate-1 confirm, achat-urgent fast-track, and the reorder
// opt-in. All on the pharmacy DS (primitives + Domain); code-split behind the
// existing <Suspense fallback={<PageLoader />}> boundary.
const CommercialCatalogueBrowse = lazy(
  () => import("./pages/commercial/CatalogueBrowse.jsx"),
);
const CommercialRfqBroadcast = lazy(
  () => import("./pages/commercial/RfqBroadcast.jsx"),
);
const CommercialRfqComparison = lazy(
  () => import("./pages/commercial/RfqComparison.jsx"),
);
// Refonte WP-33 (ux/03 §3.2-§3.5) — the pharmacy bon de commande screens: journal (tree-grid),
// editor (/nouveau, a brouillon), document + send dialog.
const JournalBonsCommandePage = lazy(() => import("./pages/bon-commande/JournalBonsCommandePage.jsx"));
const BonCommandeEditeurPage = lazy(() => import("./pages/bon-commande/BonCommandeEditeurPage.jsx"));
const BonCommandePage = lazy(() => import("./pages/bon-commande/BonCommandePage.jsx"));
const CommercialDelegueConfirm = lazy(
  () => import("./pages/commercial/DelegueConfirm.jsx"),
);
const CommercialAchatUrgent = lazy(
  () => import("./pages/commercial/AchatUrgent.jsx"),
);
const CommercialReorderSettings = lazy(
  () => import("./pages/commercial/ReorderSettings.jsx"),
);
// ── Module 05 (Logistics) — pharmacy "Incoming Deliveries" (E2) ───────────────
// Read-only view of en-route shipments destined for the pharmacy (BL n°,
// supplier, transport status, last tracking event, cold-chain badge) + a drawer
// with the EvenementColis timeline and the cold-chain readings. Pharmacy DS;
// code-split behind the existing <Suspense fallback={<PageLoader />}> boundary.
const LivraisonsIncoming = lazy(
  () => import("./pages/livraison/IncomingDeliveries.jsx"),
);
// Dermo-Conseiller — caissier guided session (D4). Deterministic taxonomy
// wizard, gated by AIDE_AU_CONSEIL with a soft 402 preview (never a dead page).
// Lazy so it stays off the initial bundle; behind the normal auth guard.
const DermoConseilSession = lazy(
  () => import("./pages/conseil/DermoConseilSession.jsx"),
);
// ── Paiement result pages (post-Chargily redirect) ────────────────────────────
// Reached right after the browser redirect from a Chargily checkout. Lazy so
// they don't weigh on the initial bundle; behind the normal auth guard.
const PaiementSucces = lazy(
  () => import("./pages/paiement/PaiementSucces.jsx"),
);
const PaiementEchec = lazy(
  () => import("./pages/paiement/PaiementEchec.jsx"),
);
// ── Helpers ───────────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        opacity: 0.4,
      }}
    >
      <svg
        style={{ animation: "spin .9s linear infinite" }}
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M21 12a9 9 0 11-6.219-8.56" />
      </svg>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

interface GuardProps {
  children: React.ReactNode;
  /** Pattern of `config/routeAccess.config.js` ROUTE_ACCESS (pharmacy shell). */
  route?: string;
  /** Explicit roles — the fournisseur portal only (SUPPLIER_ROLES, A-20). */
  roles?: readonly string[];
}

// Role-aware landing path. Any supplier role (the 7 granular FOURNISSEUR_* roles
// plus the legacy FOURNISSEUR / LABORATOIRE) lands on the supplier portal; the
// pharmacy/admin redirects are unchanged (compatibility).
function homeForRole(role?: string): string {
  if (role === "ADMIN_PHARMAKON") return "/admin/dashboard";
  if (role === "CAISSIER") return "/caissier";
  if (role && SUPPLIER_ROLES.includes(role)) return "/portail-fournisseur";
  return "/dashboard";
}

function RequireAuth({ children, route, roles: explicitRoles }: GuardProps) {
  const { user } = useAuthStore();
  const loc = useLocation();
  // One table for the guard and the menu (ux/01 §6.6): a route reads its
  // roles from ROUTE_ACCESS, so the menu can never offer a page this refuses.
  const roles = route != null ? routeRoles(route) : explicitRoles;
  if (!user && posteOuvert()) return <Navigate to={ROUTES_SESSION_EQUIPE.CHOIX} replace />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return <>{children}</>;
}

/**
 * A legacy URL of ROUTE_REDIRECTS: same target, query string kept — or rewritten on the way when the
 * legacy page named things another way (Trésorerie TWP-28: `/achats/reglements?tab=` → the tab of the
 * path, `?fournisseur=` / `?fournisseur_id=` → the fournisseur's drawer, REDIRECTIONS_REGLEMENTS).
 */
const REECRITURES_REDIRECTIONS: Readonly<Record<string, (search: string) => string>> = REDIRECTIONS_REGLEMENTS;
function LegacyRedirect({ from, to }: { from: string; to: string }) {
  const { search, hash } = useLocation();
  const reecrire = REECRITURES_REDIRECTIONS[from];
  return <Navigate to={`${reecrire ? reecrire(search) : `${to}${search}`}${hash}`} replace />;
}

/**
 * X XWP-16 (XMD-01-23) — the Emplacements menu leaf and old links land on the « Emplacements »
 * view of /magasins, their query string kept (`?q=AA` stays a search).
 */
function EmplacementsRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`${redirectionEmplacements(search)}${hash}`} replace />;
}

function RootRedirect() {
  const { user } = useAuthStore();
  if (!user && posteOuvert()) return <Navigate to={ROUTES_SESSION_EQUIPE.CHOIX} replace />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
}

/** NotFound (ux/01 §3.12, §6.3) — the shell page, fed with the role's home. */
function NotFound() {
  const { user } = useAuthStore();
  return <NotFoundPage home={user ? homeForRole(user.role) : "/login"} />;
}

/**
 * /support dispatcher. PROPRIETAIRE / MANAGER land on the professional
 * "Mes tickets" interface (MyTickets); every other role (e.g. FOURNISSEUR)
 * keeps the generic SupportPage. A single route avoids two same-path entries
 * shadowing each other in the v6 <Routes> tree.
 */
function SupportRoute() {
  const { user } = useAuthStore();
  if (user && (user.role === "PROPRIETAIRE" || user.role === "MANAGER")) {
    return <MyTickets />;
  }
  return <SupportPage />;
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  // Mirror the resolved theme onto <html data-theme> (Novera Pro: "novera" |
  // "novera-dark"). The store already writes it on rehydrate and on every
  // change; this keeps the DOM in step if anything else touched the attribute.
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Density rehydrate (DS-v2): the themeStore writes <html data-density> only
  // inside setDensity(); on a fresh page load the persisted preference is in
  // the store but the attribute was never applied to the DOM, so the CSS
  // density tokens (--card-pad / --control-h / --row-py / --stack) fell back to
  // the comfortable :root defaults regardless of the saved choice. Re-apply the
  // persisted density on mount so a saved "compact" survives a reload.
  const density = useThemeStore((s) => s.density);
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-density",
      density ?? "comfortable",
    );
  }, [density]);

  // Module 09 — start the offline sync engine once per tab (heartbeat,
  // outbox drain, delta refresh). Behaviourally inert when the server flag
  // is off / the terminal is not enrolled.
  useEffect(() => {
    startSyncEngine();
  }, []);

  return (
    <ToastProvider>
      {/* The ONE toast host (ux/01 §3.9, AUD-UXDS-09): App level, so /login,
          /register and the fournisseur portal get the same toasts. */}
      <ToastHost />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register-fournisseur" element={<RegisterFournisseur />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path={ROUTES_SESSION_EQUIPE.CONNEXION}
          element={
            <Suspense fallback={<PageLoader />}>
              <ConnexionEquipe />
            </Suspense>
          }
        />
        <Route
          path={ROUTES_SESSION_EQUIPE.CHOIX}
          element={
            <Suspense fallback={<PageLoader />}>
              <ChoixUtilisateur />
            </Suspense>
          }
        />
        <Route path="/" element={<RootRedirect />} />

        {/* ── Fournisseur Portal (Module 00) — own modular layout, no AppShell ──
            Nested route tree mounted at /portail-fournisseur/*; supplier-role
            gating + layout live inside FournisseurRoutes. Any supplier role
            (7 granular + legacy FOURNISSEUR/LABORATOIRE) is admitted. */}
        <Route
          path="/portail-fournisseur/*"
          element={
            <RequireAuth roles={SUPPLIER_ROLES}>
              <FournisseurRoutes />
            </RequireAuth>
          }
        />

        <Route
          path="/*"
          element={
            <RequireAuth>
              <AppShell>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route index element={<RootRedirect />} />

                    {/* ── Dashboards ── DashboardRouter dispatches by role */}
                    <Route
                      path="dashboard"
                      element={
                        <RequireAuth route="/dashboard">
                          <DashboardRouter />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="caissier"
                      element={
                        <RequireAuth route="/caissier">
                          <DashboardRouter />
                        </RequireAuth>
                      }
                    />
                    {/* Trésorerie TWP-18 — /caisses is a redirect to « Comptes & caisses › Sessions de caisse »
                        (ROUTE_REDIRECTS, ADR-12): the sessions tab keeps every column and gesture. */}
                    <Route
                      path="equipe"
                      element={
                        <RequireAuth route="/equipe">
                          <EquipePage />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 1 (XpertPharm parity) — psychotrope module.
                        Médecin directory + ordonnancier register + Annexes.
                        Management surfaces are PROPRIETAIRE / MANAGER. */}
                    <Route
                      path="medecins"
                      element={
                        <RequireAuth route="/medecins">
                          <MedecinList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="ordonnancier"
                      element={
                        <RequireAuth route="/ordonnancier">
                          <OrdonnancierJournal />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="ordonnancier/annexe-12"
                      element={
                        <RequireAuth route="/ordonnancier/annexe-12">
                          <OrdonnancierAnnexe variant="12" />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="ordonnancier/annexe-13"
                      element={
                        <RequireAuth route="/ordonnancier/annexe-13">
                          <OrdonnancierAnnexe variant="13" />
                        </RequireAuth>
                      }
                    />
                    {/* X XWP-55 — the register with a delivery's drawer open (static
                        annexe paths rank before :id). */}
                    <Route
                      path="ordonnancier/:id"
                      element={
                        <RequireAuth route="/ordonnancier/:id">
                          <OrdonnancierJournal />
                        </RequireAuth>
                      }
                    />

                    {/* Batch-1 vague 2 (Q2) — ventes de secours : la surface de
                        vieillissement. Lecture pour le manager (il prépare les
                        achats qui soldent les dettes) ; la régularisation, elle,
                        est gardée dans la page ET côté serveur — propriétaire
                        seul. Le CAISSIER est absent : il vend en secours, il ne
                        gouverne pas les dettes. */}
                    <Route
                      path="secours"
                      element={
                        <RequireAuth route="/secours">
                          <SecoursPage />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 2 (XpertPharm parity) — shortage & reorder */}
                    <Route
                      path="manquants"
                      element={
                        <RequireAuth route="/manquants">
                          <ManquantList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="manquants/assistant-commande"
                      element={
                        <RequireAuth route="/manquants/assistant-commande">
                          <OrderAssistant />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 4 (XpertPharm parity) — expiry risk module */}
                    <Route
                      path="stock/risque-peremption"
                      element={
                        <RequireAuth route="/stock/risque-peremption">
                          <PeremptionRisk />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 5 (XpertPharm parity) — loyalty programme */}
                    <Route
                      path="fidelite"
                      element={
                        <RequireAuth route="/fidelite">
                          <FideliteHub />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 7 (XpertPharm parity) — sales-document lifecycle */}
                    <Route
                      path="facturation"
                      element={
                        <RequireAuth route="/facturation">
                          <FacturationHub />
                        </RequireAuth>
                      }
                    />

                    {/* Trésorerie TWP-18 — « Comptes & caisses » (C1-C6, UX 02 §4-§9): the tree and the
                        « Sessions de caisse » tab, two path tabs of one page (UXD-01). */}
                    <Route
                      path="tresorerie/comptes"
                      element={
                        <RequireAuth route="/tresorerie/comptes">
                          <ComptesCaissesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="tresorerie/comptes/sessions"
                      element={
                        <RequireAuth route="/tresorerie/comptes/sessions">
                          <ComptesCaissesPage />
                        </RequireAuth>
                      }
                    />
                    {/* Trésorerie TWP-20 — « Mouvements » (UX 03 §9 J1, minimal until TWP-29): the operations
                        list, the one OperationForm (M1), the operation drawer (M3) and its L1 stack. */}
                    <Route
                      path="tresorerie/mouvements"
                      element={
                        <RequireAuth route="/tresorerie/mouvements">
                          <MouvementsPage />
                        </RequireAuth>
                      }
                    />
                    {/* Trésorerie TWP-26 — « Règlements » (UX 03 §3-§8): /tresorerie/reglements lands on its tab
                        (« Soldes fournisseurs » for whoever may settle, else the journal — TA-25); tabs in the path. */}
                    <Route
                      path="tresorerie/reglements"
                      element={
                        <RequireAuth route="/tresorerie/reglements">
                          <ReglementsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="tresorerie/reglements/:onglet"
                      element={
                        <RequireAuth route="/tresorerie/reglements/:onglet">
                          <ReglementsPage />
                        </RequireAuth>
                      }
                    />
                    {/* Trésorerie TWP-23 — « Paramètres de trésorerie » (UX 02 §15, TD-202): the « Trésorerie »
                        section of the ONE Paramètres workspace (refonte WP-20), the same component as
                        /parametres/tresorerie; MANAGER reads it here (TD-152 — /parametres stays P-only). */}
                    <Route
                      path="tresorerie/parametres"
                      element={
                        <RequireAuth route="/tresorerie/parametres">
                          <PharmacieSettings sectionFixe="tresorerie" entree="/tresorerie/parametres" />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="tresorerie/parametres/:onglet"
                      element={
                        <RequireAuth route="/tresorerie/parametres/:onglet">
                          <PharmacieSettings sectionFixe="tresorerie" entree="/tresorerie/parametres" />
                        </RequireAuth>
                      }
                    />
                    {/* Phase 8 (XpertPharm parity) — treasury & cash management */}
                    <Route
                      path="tresorerie"
                      element={
                        <RequireAuth route="/tresorerie">
                          <TresorerieHub />
                        </RequireAuth>
                      }
                    />
                    {/* Trésorerie TWP-22 — « Mouvements de ma caisse » (UX 02 §13.5): the caissier's
                        own journal (transfers included) under the « Ma caisse » card; P / M are
                        redirected to the officine journal /tresorerie/mouvements (R-09). */}
                    {/* Batch-1 vague 3 — le compte PERMANENT de l'officine.
                        PROPRIETAIRE / MANAGER : le serveur réserve en plus la
                        création d'emplacements et le paiement des charges au
                        PROPRIETAIRE (`tresorerie_pharmacie:manage`,
                        `charge:pay`), le client ne fait que masquer. */}
                    <Route
                      path="caisse-centrale"
                      element={
                        <RequireAuth route="/caisse-centrale">
                          <CaisseCentrale />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="ma-tresorerie"
                      element={
                        <RequireAuth route="/ma-tresorerie">
                          <MaCaissePage />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 10 (XpertPharm parity) — financial reports */}
                    <Route
                      path="rapports-finance"
                      element={
                        <RequireAuth route="/rapports-finance">
                          <RapportsFinance />
                        </RequireAuth>
                      }
                    />

                    {/* Phase 11 (XpertPharm parity) — multi-warehouse */}
                    <Route
                      path="magasins"
                      element={
                        <RequireAuth route="/magasins">
                          <MagasinsWorkspace />
                        </RequireAuth>
                      }
                    />

                    {/* TIP (2026-08) — réseau de confrères. Lecture ouverte au
                        MANAGER (les documents à venir portent des prix d'achat,
                        donc jamais le CAISSIER) ; nouer / rompre un lien reste
                        au PROPRIETAIRE, gardé par le serveur ET masqué dans la
                        page. Le jeton d'invitation arrive en `?jeton=` sur cette
                        même route. */}
                    <Route
                      path="confreres"
                      element={
                        <RequireAuth route="/confreres">
                          <ConfreresHub />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="support"
                      element={
                        <RequireAuth route="/support">
                          <SupportRoute />
                        </RequireAuth>
                      }
                    />

                    {/* Journal d'audit — PROPRIETAIRE / MANAGER only.
                        Backend GET /audit is gated user:read and scopes
                        responses to req.user.pharmacie_id automatically. */}
                    {/* Module 09 — offline sync review queue (owner/manager) */}
                    <Route
                      path="sync/a-verifier"
                      element={
                        <RequireAuth route="/sync/a-verifier">
                          <SyncConflictsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <RequireAuth route="/audit">
                          <AuditPage />
                        </RequireAuth>
                      }
                    />

                    {/* Abonnement — full subscription dashboard (Chargily).
                        Caissier sees /moncompte for personal profile only. */}
                    <Route
                      path="abonnement"
                      element={
                        <RequireAuth route="/abonnement">
                          <AbonnementPage />
                        </RequireAuth>
                      }
                    />

                    {/* /moncompte → personal profile (Caissier-facing).
                        Aliased to the legacy MonCompte page; PROPRIETAIRE
                        users should land on /abonnement instead. */}
                    <Route
                      path="moncompte"
                      element={
                        <RequireAuth route="/moncompte">
                          <MonCompte />
                        </RequireAuth>
                      }
                    />

                    {/* ── Paiement — résultat après redirection Chargily ──
                        Atterrissage du navigateur après un checkout. Auth
                        requise (sans restriction de rôle) pour qu'un
                        PROPRIETAIRE redirigé arrive bien sur la page. La page
                        succès rafraîchit la licence/les modules au montage. */}
                    <Route
                      path="paiement/succes"
                      element={
                        <RequireAuth route="/paiement/succes">
                          <PaiementSucces />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="paiement/echec"
                      element={
                        <RequireAuth route="/paiement/echec">
                          <PaiementEchec />
                        </RequireAuth>
                      }
                    />

                    {/* ── Ventes / POS ── */}
                    <Route
                      path="caisse"
                      element={
                        <RequireAuth route="/caisse">
                          <CaisseLibre />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="chifa"
                      element={
                        <RequireAuth route="/chifa">
                          <VenteChifa />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="ventes"
                      element={
                        <RequireAuth route="/ventes">
                          <VenteList />
                        </RequireAuth>
                      }
                    />

                    {/* ── Achats ── static before :id */}
                    <Route
                      path="achats"
                      element={
                        <RequireAuth route="/achats">
                          <JournalAchats />
                        </RequireAuth>
                      }
                    />
                    {/* Refonte WP-52 — S5, static before :id */}
                    <Route
                      path="achats/journal-detaille"
                      element={
                        <RequireAuth route="/achats/journal-detaille">
                          <JournalDetailleAchats />
                        </RequireAuth>
                      }
                    />
                    {/* Refonte WP-31 — bons de retour fournisseur (ux/03 §0.3), static before :id */}
                    <Route
                      path="achats/bons-retour"
                      element={
                        <RequireAuth route="/achats/bons-retour">
                          <JournalBonsRetourPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/bons-retour/nouveau"
                      element={
                        <RequireAuth route="/achats/bons-retour/nouveau">
                          <BonRetourEditeurPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/bons-retour/:id"
                      element={
                        <RequireAuth route="/achats/bons-retour/:id">
                          <BonRetourPage />
                        </RequireAuth>
                      }
                    />
                    {/* Refonte WP-35 — réclamations fournisseur (ux/03 §0.3, §5), static before :id */}
                    <Route
                      path="achats/reclamations"
                      element={
                        <RequireAuth route="/achats/reclamations">
                          <JournalReclamationsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/reclamations/nouvelle"
                      element={
                        <RequireAuth route="/achats/reclamations/nouvelle">
                          <ReclamationEditeurPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/reclamations/:id"
                      element={
                        <RequireAuth route="/achats/reclamations/:id">
                          <ReclamationPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/nouveau"
                      element={
                        <RequireAuth route="/achats/nouveau">
                          <NouvelAchatPage />
                        </RequireAuth>
                      }
                    />
                    {/* S1 hydrated from a BROUILLON (ux/02 §4.1): any other status
                        is sent to /achats/:id. Plus spécifique que /achats/:id,
                        donc déclaré avant pour la résolution de route. */}
                    <Route
                      path="achats/:id/modifier"
                      element={
                        <RequireAuth route="/achats/:id/modifier">
                          <NouvelAchatPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achats/:id"
                      element={
                        <RequireAuth route="/achats/:id">
                          <FactureAchatPage />
                        </RequireAuth>
                      }
                    />
                    {/* Refonte WP-05 review round 1 — P+M only, as the server's
                        efacture.read: an e-facture carries purchase prices and
                        HT totals, which a CAISSIER never receives (ux/04 §0.6). */}
                    <Route
                      path="efactures-recu"
                      element={
                        <RequireAuth route="/efactures-recu">
                          <EFacturesReceived />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="efactures-recu/:id"
                      element={
                        <RequireAuth route="/efactures-recu/:id">
                          <EFactureRecuDetail />
                        </RequireAuth>
                      }
                    />
                    {/* E-Facture ERP — Achat urgent (static before :id) +
                        post-IMPORTEE BL/BR workspace. */}
                    <Route
                      path="achat/urgence"
                      element={
                        <RequireAuth route="/achat/urgence">
                          <EmergencyOrderPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="achat/efacture/:id/workspace"
                      element={
                        <RequireAuth route="/achat/efacture/:id/workspace">
                          <EFactureWorkspace />
                        </RequireAuth>
                      }
                    />

                    {/* ── Avoirs ── static before :id */}
                    <Route
                      path="avoirs"
                      element={
                        <RequireAuth route="/avoirs">
                          <JournalAvoirsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="avoirs/nouveau"
                      element={
                        <RequireAuth route="/avoirs/nouveau">
                          <NouvelAvoirPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="avoirs/:id"
                      element={
                        <RequireAuth route="/avoirs/:id">
                          <JournalAvoirsPage />
                        </RequireAuth>
                      }
                    />

                    {/* ── Stock ── */}
                    <Route
                      path="stock"
                      element={
                        <RequireAuth route="/stock">
                          <StockDashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="stock/corrections"
                      element={
                        <RequireAuth route="/stock/corrections">
                          <CorrectionsJournal />
                        </RequireAuth>
                      }
                    />
                    {/* Batch 4 (Cluster D) — product search, all three roles.
                        UI gating rides this route guard + the server's
                        stock.read RBAC (no permissions.config change). */}
                    <Route
                      path="recherche-produits"
                      element={
                        <RequireAuth route="/recherche-produits">
                          <RechercheProduitPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="emplacements"
                      element={
                        <RequireAuth route="/emplacements">
                          <EmplacementsRedirect />
                        </RequireAuth>
                      }
                    />

                    {/* ── Produits ── static before :id */}
                    <Route
                      path="produits"
                      element={
                        <RequireAuth route="/produits">
                          <ProduitList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="produits/nouveau"
                      element={
                        <RequireAuth route="/produits/nouveau">
                          <ProduitForm />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="produits/:id"
                      element={
                        <RequireAuth route="/produits/:id">
                          <ProduitForm editMode />
                        </RequireAuth>
                      }
                    />

                    {/* ── Fournisseurs ── */}
                    <Route
                      path="fournisseurs"
                      element={
                        <RequireAuth route="/fournisseurs">
                          <FournisseurList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="fournisseurs/nouveau"
                      element={
                        <RequireAuth route="/fournisseurs/nouveau">
                          <FournisseurForm />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="fournisseurs/:id"
                      element={
                        <RequireAuth route="/fournisseurs/:id">
                          <FournisseurForm editMode />
                        </RequireAuth>
                      }
                    />

                    {/* ── Settings + Admin ── */}
                    <Route
                      path="parametres"
                      element={
                        <RequireAuth route="/parametres">
                          <PharmacieSettings />
                        </RequireAuth>
                      }
                    />
                    {/* WP-20 — sections and onglets of the one settings workspace. */}
                    <Route
                      path="parametres/:section"
                      element={
                        <RequireAuth route="/parametres/:section">
                          <PharmacieSettings />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="parametres/:section/:onglet"
                      element={
                        <RequireAuth route="/parametres/:section/:onglet">
                          <PharmacieSettings />
                        </RequireAuth>
                      }
                    />
                    {/* B2G-ADM-P2-10 — bare /admin has no screen (the dashboard lives at
                        admin/dashboard), so the natural entry URL dead-ended on the 404 page for
                        a platform admin. Redirect the index to the dashboard (guarded there). */}
                    <Route
                      path="admin"
                      element={
                        <RequireAuth route="/admin">
                          <Navigate to="/admin/dashboard" replace />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/dashboard"
                      element={
                        <RequireAuth route="/admin/dashboard">
                          <AdminDashboard />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/pharmacies"
                      element={
                        <RequireAuth route="/admin/pharmacies">
                          <AccountsQueue />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/pharmacies/:id/licences"
                      element={
                        <RequireAuth route="/admin/pharmacies/:id/licences">
                          <LicenceManager />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/fournisseurs"
                      element={
                        <RequireAuth route="/admin/fournisseurs">
                          <AdminFournisseurs />
                        </RequireAuth>
                      }
                    />
                    {/* F-2 (B2G) — the NOVERA macro-admin vertical consoles */}
                    <Route
                      path="admin/verticals/novanalyse"
                      element={
                        <RequireAuth route="/admin/verticals/novanalyse">
                          <NovanalyseConsole />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/verticals/cabinets"
                      element={
                        <RequireAuth route="/admin/verticals/cabinets">
                          <CabinetsConsole />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/verticals/ailaji"
                      element={
                        <RequireAuth route="/admin/verticals/ailaji">
                          <AilajiConsole />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/licences"
                      element={
                        <RequireAuth route="/admin/licences">
                          <AdminLicences />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/billing"
                      element={
                        <RequireAuth route="/admin/billing">
                          <BillingDashboard />
                        </RequireAuth>
                      }
                    />
                    {/* Fournisseur ERP Module 08 — supplier subscriptions */}
                    <Route
                      path="admin/abonnements-fournisseurs"
                      element={
                        <RequireAuth route="/admin/abonnements-fournisseurs">
                          <AbonnementsFournisseurs />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/pricing"
                      element={
                        <RequireAuth route="/admin/pricing">
                          <PricingConfig />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/modules"
                      element={
                        <RequireAuth route="/admin/modules">
                          <ModulesManager />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/cnas"
                      element={
                        <RequireAuth route="/admin/cnas">
                          <CNASDbManager />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/cnas/import"
                      element={
                        <RequireAuth route="/admin/cnas/import">
                          <CNASImport />
                        </RequireAuth>
                      }
                    />
                    {/* ── Module 4: Monitoring ── */}
                    <Route
                      path="admin/monitor/health"
                      element={
                        <RequireAuth route="/admin/monitor/health">
                          <SystemHealth />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/monitor/audit"
                      element={
                        <RequireAuth route="/admin/monitor/audit">
                          <AuditExplorer />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/monitor/errors"
                      element={
                        <RequireAuth route="/admin/monitor/errors">
                          <ErrorTracking />
                        </RequireAuth>
                      }
                    />
                    {/* ── Module 5: Users & Security ── */}
                    <Route
                      path="admin/monitor/users"
                      element={
                        <RequireAuth route="/admin/monitor/users">
                          <UsersList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/monitor/security"
                      element={
                        <RequireAuth route="/admin/monitor/security">
                          <SecurityLog />
                        </RequireAuth>
                      }
                    />
                    {/* ── Module 6: Communications ── */}
                    <Route
                      path="admin/notifications"
                      element={
                        <RequireAuth route="/admin/notifications">
                          <AdminNotifications />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/broadcasts"
                      element={
                        <RequireAuth route="/admin/broadcasts">
                          <NotifBroadcast />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/tickets"
                      element={
                        <RequireAuth route="/admin/tickets">
                          <TicketSupport />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="decision"
                      element={
                        <RequireAuth route="/decision">
                          <AideDecision />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="decision/besoin"
                      element={
                        <RequireAuth route="/decision/besoin">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <BesoinPage />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="decision/offres"
                      element={
                        <RequireAuth route="/decision/offres">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <OffresPage />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="decision/offres/historique"
                      element={
                        <RequireAuth route="/decision/offres/historique">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <OffresPage initialTab="historique" />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="aide-decision/config"
                      element={
                        <RequireAuth route="/aide-decision/config">
                          <DecisionConfigPage />
                        </RequireAuth>
                      }
                    />
                    {/* E-Facture ERP — offer comparison + counter-offer
                        (gated AIDE_DECISION inside each page via ModuleGate). */}
                    <Route
                      path="aide-decision/pre-commandes/:id/comparison"
                      element={
                        <RequireAuth route="/aide-decision/pre-commandes/:id/comparison">
                          <OfferComparisonPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="aide-decision/offres/:id/counter"
                      element={
                        <RequireAuth route="/aide-decision/offres/:id/counter">
                          <CounterOfferPage />
                        </RequireAuth>
                      }
                    />

                    {/* ── Module 03 (Commercial) — pharmacy-facing ──
                        Supplier-catalogue browse + order builder, RFQ
                        broadcast/compare, bilateral BC confirm, délégué Gate-1
                        confirm, achat-urgent fast-track, reorder opt-in. Static
                        paths before :id so they resolve correctly. PROPRIETAIRE
                        / MANAGER only (matches the decision surfaces). */}
                    <Route
                      path="commercial/catalogue"
                      element={
                        <RequireAuth route="/commercial/catalogue">
                          <CommercialCatalogueBrowse />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/appel-offres"
                      element={
                        <RequireAuth route="/commercial/appel-offres">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <CommercialRfqBroadcast />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    {/* X XWP-76 (CP §15.9) — the appel's detail page; the /comparaison links already sent keep working. */}
                    <Route
                      path="commercial/appel-offres/:id"
                      element={
                        <RequireAuth route="/commercial/appel-offres/:id">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <CommercialRfqComparison />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/appel-offres/:id/comparaison"
                      element={
                        <RequireAuth route="/commercial/appel-offres/:id/comparaison">
                          <ModuleGate moduleCode="AIDE_DECISION">
                            <CommercialRfqComparison />
                          </ModuleGate>
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/bons-commande"
                      element={
                        <RequireAuth route="/commercial/bons-commande">
                          <JournalBonsCommandePage />
                        </RequireAuth>
                      }
                    />
                    {/* static segment declared before :id (ux/03 §0.3) */}
                    <Route
                      path="commercial/bons-commande/nouveau"
                      element={
                        <RequireAuth route="/commercial/bons-commande/nouveau">
                          <BonCommandeEditeurPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/bons-commande/:id"
                      element={
                        <RequireAuth route="/commercial/bons-commande/:id">
                          <BonCommandePage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/precommandes-a-confirmer"
                      element={
                        <RequireAuth route="/commercial/precommandes-a-confirmer">
                          <CommercialDelegueConfirm />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/achat-urgent"
                      element={
                        <RequireAuth route="/commercial/achat-urgent">
                          <CommercialAchatUrgent />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="commercial/reapprovisionnement"
                      element={
                        <RequireAuth route="/commercial/reapprovisionnement">
                          <CommercialReorderSettings />
                        </RequireAuth>
                      }
                    />

                    {/* ── Module 05 (Logistics) — pharmacy "Incoming Deliveries"
                        (E2). Read-only en-route shipment tracking + cold-chain.
                        PROPRIETAIRE / MANAGER only (matches the Commercial group). */}
                    <Route
                      path="livraisons/incoming"
                      element={
                        <RequireAuth route="/livraisons/incoming">
                          <LivraisonsIncoming />
                        </RequireAuth>
                      }
                    />

                    {/* Trésorerie TWP-28 (TD-131) — « Mon relevé de compte » is retired: /mon-releve is a
                        legacy redirect (ROUTE_REDIRECTS) to « Règlements › Soldes fournisseurs », the
                        relevé being the fournisseur's drawer there (RC-115, RC-48). */}

                    {/* Legacy "Assistant IA" (CHATBOT) entry point. The module
                        was consolidated into Aide au Conseil, so /conseil now
                        renders the canonical Dermo-Conseiller / Aide-au-Conseil
                        session (soft-gated by AIDE_AU_CONSEIL in-page) — bookmarked
                        /conseil links keep working and never hit a dead page. */}
                    <Route
                      path="conseil"
                      element={
                        <RequireAuth route="/conseil">
                          <DermoConseilSession />
                        </RequireAuth>
                      }
                    />
                    {/* Dermo-Conseiller — caissier guided session (D4). Soft-gated
                        by AIDE_AU_CONSEIL inside the page (402 preview), so the
                        route stays open to P / M / C and never dead-ends. */}
                    <Route
                      path="dermo-conseiller"
                      element={
                        <RequireAuth route="/dermo-conseiller">
                          <DermoConseilSession />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/produits-base"
                      element={
                        <RequireAuth route="/admin/produits-base">
                          <BaseProduits />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="admin/produits-base/import"
                      element={
                        <RequireAuth route="/admin/produits-base/import">
                          <BaseProduitImport />
                        </RequireAuth>
                      }
                    />
                    {/* علاجي — admin reference registers (B5) */}
                    <Route
                      path="admin/aalaji/referentiels"
                      element={
                        <RequireAuth route="/admin/aalaji/referentiels">
                          <ReferentielsAalaji />
                        </RequireAuth>
                      }
                    />
                    {/* علاجي — pharmacy hub, preparations console, settings
                        (B_PH.UI / B6 / B_PH.SETTINGS). Console open to the
                        counter (CAISSIER+); settings page MANAGER+, with the
                        activation flip enforced PROPRIETAIRE server-side. */}
                    <Route
                      path="aalaji"
                      element={
                        <RequireAuth route="/aalaji">
                          <AalajiHub />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="aalaji/preparations"
                      element={
                        <RequireAuth route="/aalaji/preparations">
                          <PreparationsConsole />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="aalaji/parametres"
                      element={
                        <RequireAuth route="/aalaji/parametres">
                          <ParametresAalaji />
                        </RequireAuth>
                      }
                    />

                    {/* ── Feature batch 2026-05 ── */}
                    <Route
                      path="clients"
                      element={
                        <RequireAuth route="/clients">
                          <ClientList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="clients/nouveau"
                      element={
                        <RequireAuth route="/clients/nouveau">
                          <ClientForm />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="clients/:id/edit"
                      element={
                        <RequireAuth route="/clients/:id/edit">
                          <ClientForm />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="clients/:id"
                      element={
                        <RequireAuth route="/clients/:id">
                          <ClientDetail />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="credits"
                      element={
                        <RequireAuth route="/credits">
                          <JournalCreditsPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="credits/:id"
                      element={
                        <RequireAuth route="/credits/:id">
                          <JournalCreditsPage />
                        </RequireAuth>
                      }
                    />
                    {/* F1 — Instance à terme CHIFA (no-prescription / has-CHIFA-card
                        deferred sale, linked to the Vente CHIFA settlement path).
                        X XWP-21 — the journal, and the journal with an instance's drawer open. */}
                    <Route
                      path="instances-chifa"
                      element={
                        <RequireAuth route="/instances-chifa">
                          <JournalInstancesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="instances-chifa/:id"
                      element={
                        <RequireAuth route="/instances-chifa/:id">
                          <JournalInstancesPage />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="instances-chifa/nouveau"
                      element={
                        <RequireAuth route="/instances-chifa/nouveau">
                          <InstanceChifaCreate />
                        </RequireAuth>
                      }
                    />
                    {/* F2 — Retour (customer product return). Static routes
                        before :id so /retours/nouveau resolves correctly. */}
                    <Route
                      path="retours"
                      element={
                        <RequireAuth route="/retours">
                          <RetourList />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="retours/nouveau"
                      element={
                        <RequireAuth route="/retours/nouveau">
                          <NouveauRetour />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="retours/:id"
                      element={
                        <RequireAuth route="/retours/:id">
                          <RetourDetail />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="decision/etat-rotation"
                      element={
                        <RequireAuth route="/decision/etat-rotation">
                          <EtatRotationPage />
                        </RequireAuth>
                      }
                    />
                    {/* Phase 3 — Enterprise Analytics / BI platform */}
                    <Route
                      path="analytics"
                      element={
                        <RequireAuth route="/analytics">
                          <AnalyticsPage />
                        </RequireAuth>
                      }
                    />
                    {/* Feature batch 4 — médecin analytics drill-down */}
                    <Route
                      path="analytics/medecins/:id"
                      element={
                        <RequireAuth route="/analytics/medecins/:id">
                          <MedecinDetail />
                        </RequireAuth>
                      }
                    />

                    {/* Legacy URLs (ux/01 §6.13) — query string kept. */}
                    {Object.entries(ROUTE_REDIRECTS).map(([from, to]) => (
                      <Route
                        key={from}
                        path={from.slice(1)}
                        element={<LegacyRedirect from={from} to={to} />}
                      />
                    ))}

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppShell>
            </RequireAuth>
          }
        />
      </Routes>
    </ToastProvider>
  );
}
